import { useCallback, useEffect, useRef, useState } from 'react';

export type AutoSaveStatus = 'idle' | 'unsaved' | 'saving' | 'saved' | 'error';

interface UseAutoSaveOptions {
  /** When false, scheduleSave() is a no-op (e.g. on /orders/new before persistence). */
  enabled: boolean;
  /** Persist work. Throw or reject to surface 'error' to the badge. */
  save: () => Promise<void>;
  /** Debounce window in ms. Default 1000. */
  debounceMs?: number;
  /** How long the green "Saved" pill lingers before fading. Default 1500. */
  savedDurationMs?: number;
}

/**
 * Generic incremental autosave: caller invokes `scheduleSave()` after every
 * tracked field change and the hook handles debounce + status state machine.
 *
 * The `save` callback is read through a ref, so it always sees the freshest
 * caller closure (no stale state when the debounced save fires).
 */
export function useAutoSave({
  enabled,
  save,
  debounceMs = 1000,
  savedDurationMs = 1500,
}: UseAutoSaveOptions): { saveStatus: AutoSaveStatus; scheduleSave: () => void } {
  const [saveStatus, setSaveStatus] = useState<AutoSaveStatus>('idle');
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const savedFadeRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const saveRef = useRef(save);
  useEffect(() => {
    saveRef.current = save;
  }, [save]);

  const scheduleSave = useCallback(() => {
    if (!enabled) return;

    if (savedFadeRef.current) {
      clearTimeout(savedFadeRef.current);
      savedFadeRef.current = null;
    }
    setSaveStatus('unsaved');

    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(async () => {
      setSaveStatus('saving');
      try {
        await saveRef.current();
        setSaveStatus('saved');
        savedFadeRef.current = setTimeout(() => {
          setSaveStatus('idle');
        }, savedDurationMs);
      } catch (err) {
        console.error('[useAutoSave] save failed:', err);
        setSaveStatus('error');
      }
    }, debounceMs);
  }, [enabled, debounceMs, savedDurationMs]);

  useEffect(() => {
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
      if (savedFadeRef.current) clearTimeout(savedFadeRef.current);
    };
  }, []);

  return { saveStatus, scheduleSave };
}
